/* eslint-disable no-unused-vars */
import React, {useState, useEffect, useCallback} from 'react'
import {useIntl} from 'react-intl'
import {Link, useParams, useNavigate, useLocation} from 'react-router-dom'
import {PageTitle} from '../../../core'

import {
  KTCard,
  KTCardBody,
  KTCardHeader,
  Notes,
  DeleteModal,
  ArchiveModal,
  DataTables,
  Select,
  PurchaseProposal,
  PurchaseProposalRequest,
  Loading,
} from '../../../components'
import {useDispatch, useSelector} from 'react-redux'
import {
  purchaseRequestGetId,
  purchaseRequestUpdateEvaluationResult,
  purchaseRequestDelete,
  purchaseRequestArchiving,
  purchaseRequestGetAllWithAllSubInfo,
} from '../../../redux/slices/purchase.slice'
import {SourceType, PurchaseRequestStatus, KTSVG, toAbsoluteUrl} from '../../../helpers'
import Dropdown from 'react-bootstrap/Dropdown'

import Tab from 'react-bootstrap/Tab'
import Tabs from 'react-bootstrap/Tabs'

import * as Yup from 'yup'
import {useFormik} from 'formik'

import {
  useMemoizedPageUrl,
  useMemoizedValidationSchema,
  useMemoizedFormValues,
  useMemoizedBreadcrumbs
} from '../../../utils/performanceOptimizations'

const selectPurchaseData = (state) => ({
  detail: state.purchase.detail,
  isDeleted: state.purchase.isDeleted,
  isArchived: state.purchase.isArchived,
  error: state.purchase.error,
  loading: state.purchase.loading,
  isAdded: state.purchase.isAdded,
  isRedirect: state.purchase.isRedirect,
  purchasePdf: state.purchase.purchasePdf,
  purchaseProposalRequest: state.purchase.purchaseProposalRequest,
  isUpdateEvaluationResult: state.purchase.isUpdateEvaluationResult,
})

function Detail() {
  const [show, setShow] = useState(false)
  const [showArchive, setShowArchive] = useState(false)
  const [isCollapse, setIsCollapse] = useState(true)
  const {id} = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const intl = useIntl()
  const {
    detail,
    isDeleted,
    isArchived,
    error,
    loading,
    isAdded,
    isRedirect,
    purchasePdf,
    purchaseProposalRequest,
    isUpdateEvaluationResult,
  } = useSelector(selectPurchaseData)
  const dispatch = useDispatch()

  const pageUrl = useMemoizedPageUrl(location)

  useEffect(() => {
    dispatch(purchaseRequestGetId(id))
  }, [dispatch, id, isAdded, isRedirect, purchaseProposalRequest, isUpdateEvaluationResult])

  useEffect(() => {
    if (detail) {
      formik.setFieldValue('PurchaseRequestStatusId', detail.purchaseRequestStatusId || '')
      formik.setFieldValue('EvaluationResult', detail.evaluationResult || '')
    }
  }, [detail])

  const handleDelete = useCallback(() => {
    dispatch(purchaseRequestDelete({id}))
  }, [dispatch, id])

  const handleArchive = useCallback(() => {
    dispatch(purchaseRequestArchiving({id}))
  }, [dispatch, id])

  useEffect(() => {
    if (isDeleted || isArchived) navigate(`/${pageUrl[1]}/${pageUrl[2]}`)
  }, [isDeleted, isArchived, navigate, pageUrl])

  const breadcrumbs = useMemoizedBreadcrumbs(() => [
    {
      title: intl.formatMessage({id: 'BC.REQUEST'}),
      path: '/purchase/request',
      isSeparator: false,
      isActive: false,
    },
    {
      title: '',
      path: '',
      isSeparator: true,
      isActive: false,
    },
  ], [intl])

  const validSchema = useMemoizedValidationSchema(() => Yup.object().shape({
    PurchaseRequestStatusId: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
  }), [intl])

  const formValues = useMemoizedFormValues({
    PurchaseRequestStatusId: '',
    Id: id,
    EvaluationResult: '',
  })

  const handleSubmit = useCallback(async (values, {setSubmitting, resetForm}) => {
    setSubmitting(true)
    dispatch(purchaseRequestUpdateEvaluationResult(values))
    resetForm()
  }, [dispatch])

  const formik = useFormik({
    initialValues: formValues,
    validationSchema: validSchema,
    onSubmit: handleSubmit,
  })

  const handleClickPdf = useCallback(() => {
    dispatch(purchaseRequestGetAllWithAllSubInfo({purchaseRequestId: id}))
  }, [dispatch, id])

  useEffect(() => {
    if (purchasePdf) {
      window.open(purchasePdf && purchasePdf.fileUrl, '_blank')
    }
  }, [purchasePdf])

  return (
    <>
      <PageTitle breadcrumbs={breadcrumbs}>
        {intl.formatMessage({id: 'BC.REQUEST.DETAIL'})}
      </PageTitle>

      <div className='row gy-10 gx-xl-10'>
        <div className='col-xl-9'>
          {detail && (
            <>
              <KTCard className='mb-5'>
                <KTCardBody className='p-9'>
                  <div className='d-flex flex-wrap'>
                    <div className='flex-grow-1 w-100'>
                      <div className='d-flex justify-content-between align-items-start flex-wrap mb-5 row'>
                        <div className='d-flex flex-column col-xl-7'>
                          <div className='d-flex align-items-center mb-2'>
                            <div className='text-gray-900 fs-2 fw-bold me-1'>{detail.name}</div>

                            <div className='ms-5'></div>
                          </div>

                          <div className='d-flex flex-wrap fw-semibold fs-6 mb-4 pe-2'>
                            {detail?.projectName && (
                              <>
                                <div className='d-flex align-items-center text-gray-400 me-5 mb-2'>
                                  <KTSVG
                                    path='/images/icons/nav/Projects.svg'
                                    className='svg-icon svg-icon-2 fs-4 me-1'
                                  />
                                  {detail.projectName}
                                </div>
                              </>
                            )}

                            {detail?.companyName && (
                              <>
                                <div className='d-flex align-items-center text-gray-400 me-5 mb-2'>
                                  <KTSVG
                                    path='/images/icons/duotune/finance/fin006.svg'
                                    className='svg-icon svg-icon-2 fs-4 me-1'
                                  />
                                  {detail.companyName}
                                </div>
                              </>
                            )}

                            {detail?.relatedStaffName && (
                              <>
                                <div className='d-flex align-items-center text-gray-400 me-5 mb-2'>
                                  <KTSVG
                                    path='/images/icons/nav/Staff.svg'
                                    className='svg-icon svg-icon-2 fs-4 me-1'
                                  />
                                  {detail.relatedStaffName}
                                </div>
                              </>
                            )}
                          </div>
                        </div>

                        <div className='d-flex gap-3 align-items-center col-xl-5 justify-content-end'>
                          <Link
                            to={`/${pageUrl[1]}/${pageUrl[2]}/edit/${id}`}
                            className='btn btn-sm btn-primary align-self-center'
                          >
                            <KTSVG
                              path='/images/icons/duotune/general/gen055.svg'
                              className='svg-icon-2'
                            />
                            {intl.formatMessage({id: 'BUTTON.EDIT'})}
                          </Link>
                          <Dropdown autoClose='outside' align={{lg: 'end'}}>
                            <Dropdown.Toggle
                              variant='secondary'
                              size='sm'
                              className='iconless ps-3 pe-3'
                            >
                              <KTSVG
                                path='/images/icons/duotune/general/gen053.svg'
                                className='svg-icon-2'
                              />
                            </Dropdown.Toggle>

                            <Dropdown.Menu className='menu menu-sub menu-sub-dropdown menu-rounded menu-gray-800 menu-state-bg-light-primary fw-semibold py-3'>
                              <Dropdown.Item onClick={handleClickPdf} className='menu-item px-3'>
                                <div className='menu-content text-muted pb-2 px-3 fs-7 text-uppercase'>
                                  {intl.formatMessage({id: 'BUTTON.PDF.DOWNLOAD'})}
                                </div>
                              </Dropdown.Item>
                              <Dropdown.Item
                                onClick={() => setShowArchive(true)}
                                className='menu-item px-3'
                              >
                                <div className='menu-content text-muted pb-2 px-3 fs-7 text-uppercase'>
                                  {intl.formatMessage({id: 'BUTTON.ARCHIVE'})}
                                </div>
                              </Dropdown.Item>
                              <Dropdown.Item
                                onClick={() => setShow(true)}
                                className='menu-item px-3'
                              >
                                <div className='menu-content text-muted pb-2 px-3 fs-7 text-uppercase'>
                                  {intl.formatMessage({id: 'BUTTON.DELETE'})}
                                </div>
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        </div>
                      </div>
                    </div>

                    <div className='d-flex flex-wrap flex-stack'>
                      <div className='d-flex flex-column flex-grow-1 pe-8'>
                        <div className='d-flex flex-wrap'>
                          <div className='border border-gray-300 border-dashed rounded min-w-125px py-3 px-4 me-6 mb-3'>
                            <div className='fw-semibold fs-6 text-gray-400'>
                              {intl.formatMessage({id: 'REQUEST.CREATE.PURCHASEREQUESTSTATUSID'})}
                            </div>
                            <div className='fw-bold fs-4'>{detail?.purchaseRequestStatusName}</div>
                          </div>

                          <div className='border border-gray-300 border-dashed rounded min-w-125px py-3 px-4 me-6 mb-3'>
                            <div className='fw-semibold fs-6 text-gray-400'>
                              {intl.formatMessage({id: 'REQUEST.GET.DELAYDATE'})}
                            </div>
                            <div className='fw-bold fs-4'>
                              {detail?.delayDay == 0
                                ? '-'
                                : detail?.delayDay < 0
                                ? detail?.delayDay * -1 +
                                  intl.formatMessage({id: 'PROJECT.DAYPASSED'})
                                : detail?.delayDay + intl.formatMessage({id: 'PROJECT.DAYLEFT'})}
                            </div>
                          </div>
                          {detail?.editDate && (
                            <>
                              <div className='border border-gray-300 border-dashed rounded min-w-125px py-3 px-4 me-6 mb-3'>
                                <div className='fw-semibold fs-6 text-gray-400'>
                                  {intl.formatMessage({id: 'REQUEST.GET.EDITDATE'})}
                                </div>
                                <div className='fw-bold fs-4'>
                                  {intl.formatDate(new Date(detail?.editDate), {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric',
                                  })}
                                </div>
                              </div>
                            </>
                          )}
                          {detail?.dueDate && (
                            <>
                              <div className='border border-gray-300 border-dashed rounded min-w-125px py-3 px-4 me-6 mb-3'>
                                <div className='fw-semibold fs-6 text-gray-400'>
                                  {intl.formatMessage({id: 'REQUEST.GET.DUEDATE'})}
                                </div>
                                <div className='fw-bold fs-4'>
                                  {intl.formatDate(new Date(detail?.dueDate), {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric',
                                  })}
                                </div>
                              </div>
                            </>
                          )}

                          {detail?.fileUrl && (
                            <div className='border border-gray-300 border-dashed rounded min-w-125px py-3 px-4 me-6 mb-3'>
                              <div className='fw-semibold fs-6 text-gray-400'>
                                {intl.formatMessage({id: 'REQUEST.CREATE.FILE'})}
                              </div>
                              <div className='fw-bold fs-4'>
                                <a
                                  href={detail?.fileUrl}
                                  target='_blank'
                                  rel='noreferrer'
                                  className='d-flex align-items-center'
                                >
                                  <div className='symbol symbol-25px me-2'>
                                    <span className='symbol-label'>
                                      <img
                                        src={toAbsoluteUrl(
                                          '/images/icons/duotune/files/fil021.svg'
                                        )}
                                        className='h-50 align-self-center'
                                        alt=''
                                      />
                                    </span>
                                  </div>
                                </a>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  {detail?.note && (
                    <>
                      <div className='notice d-flex rounded border-primary border border-dashed  p-6'>
                        {detail?.note}
                      </div>
                    </>
                  )}
                </KTCardBody>
              </KTCard>

              <KTCard>
                <KTCardBody className='p-9'>
                  <Tabs className='nav-line-tabs' fill>
                    {detail?.purchaseProposalConfirmedItem != null && (
                      <Tab
                        eventKey='purchaseProposalConfirmedItem'
                        title={intl.formatMessage({
                          id: 'REQUEST.GET.PURCHASEPROPOSALITEMS.PURCHASEPROPOSALCONFIRMEDITEM',
                        })}
                      >
                        <div className='row p-5'>
                          <div className='col-lg-10 order-2 order-lg-1'>
                            <div className='row mb-7'>
                              <label className='col-lg-4 fw-bold text-muted'>
                                {intl.formatMessage({id: 'REQUEST.GET.PURCHASEPROPOSALITEMS.NAME'})}
                              </label>
                              <div className='col-lg-8 fv-row'>
                                <span className='fw-bold fs-6'>
                                  {detail?.purchaseProposalConfirmedItem?.name}
                                </span>
                              </div>
                            </div>
                            <div className='row mb-7'>
                              <label className='col-lg-4 fw-bold text-muted'>
                                {intl.formatMessage({
                                  id: 'REQUEST.GET.PURCHASEPROPOSALITEMS.COMPANYNAME',
                                })}
                              </label>
                              <div className='col-lg-8 fv-row'>
                                <span className='fw-bold fs-6'>
                                  {detail?.purchaseProposalConfirmedItem.companyName}
                                </span>
                              </div>
                            </div>
                            <div className='row mb-7'>
                              <label className='col-lg-4 fw-bold text-muted'>
                                {intl.formatMessage({
                                  id: 'REQUEST.GET.PURCHASEPROPOSALITEMS.PURCHASEPROPOSALSTATUSNAME',
                                })}
                              </label>
                              <div className='col-lg-8 fv-row'>
                                <span className='fw-bold fs-6'>
                                  {detail?.purchaseProposalConfirmedItem.purchaseProposalStatusName}
                                </span>
                              </div>
                            </div>
                            <div className='row mb-7'>
                              <label className='col-lg-4 fw-bold text-muted'>
                                {intl.formatMessage({
                                  id: 'REQUEST.GET.PURCHASEPROPOSALITEMS.TRANSPORTATION',
                                })}
                              </label>
                              <div className='col-lg-8 fv-row'>
                                <span className='fw-bold fs-6'>
                                  {detail?.purchaseProposalConfirmedItem.transportation}
                                </span>
                              </div>
                            </div>
                            <div className='row mb-7'>
                              <label className='col-lg-4 fw-bold text-muted'>
                                {intl.formatMessage({
                                  id: 'REQUEST.GET.PURCHASEPROPOSALITEMS.PAYMENTDETAIL',
                                })}
                              </label>
                              <div className='col-lg-8 fv-row'>
                                <span className='fw-bold fs-6'>
                                  {detail?.purchaseProposalConfirmedItem.paymentDetail}
                                </span>
                              </div>
                            </div>
                            <div className='row mb-7'>
                              <label className='col-lg-4 fw-bold text-muted'>
                                {intl.formatMessage({
                                  id: 'REQUEST.GET.PURCHASEPROPOSALITEMS.DELIVERYTIME',
                                })}
                              </label>
                              <div className='col-lg-8 fv-row'>
                                <span className='fw-bold fs-6'>
                                  {detail?.purchaseProposalConfirmedItem.deliveryTime}
                                </span>
                              </div>
                            </div>
                            <div className='row mb-7'>
                              <label className='col-lg-4 fw-bold text-muted'>
                                {intl.formatMessage({
                                  id: 'REQUEST.GET.PURCHASEPROPOSALITEMS.BRAND',
                                })}
                              </label>
                              <div className='col-lg-8 fv-row'>
                                <span className='fw-bold fs-6'>
                                  {detail?.purchaseProposalConfirmedItem.brand}
                                </span>
                              </div>
                            </div>
                            <div className='row mb-7'>
                              <label className='col-lg-4 fw-bold text-muted'>
                                {intl.formatMessage({
                                  id: 'REQUEST.GET.PURCHASEPROPOSALITEMS.TOTALPRICE',
                                })}
                              </label>
                              <div className='col-lg-8 fv-row'>
                                <span className='fw-bold fs-6'>
                                  {intl.formatNumber(
                                    detail?.purchaseProposalConfirmedItem.totalPrice,
                                    {style: 'currency', currency: 'try', minimumFractionDigits: 2, maximumFractionDigits: 4}
                                  )}
                                </span>
                              </div>
                            </div>
                            <div className='row mb-7'>
                              <label className='col-lg-4 fw-bold text-muted'>
                                {intl.formatMessage({
                                  id: 'REQUEST.GET.PURCHASEPROPOSALITEMS.TOTALPRICEWITHTAX',
                                })}
                              </label>
                              <div className='col-lg-8 fv-row'>
                                <span className='fw-bold fs-6'>
                                  {intl.formatNumber(
                                    detail?.purchaseProposalConfirmedItem.totalPriceWithTax,
                                    {style: 'currency', currency: 'try', minimumFractionDigits: 2, maximumFractionDigits: 4}
                                  )}
                                </span>
                              </div>
                            </div>
                            {detail?.purchaseProposalConfirmedItem?.proposalFileUrl && (
                              <div className='row mb-7'>
                                <label className='col-lg-4 fw-bold text-muted'>
                                  {intl.formatMessage({
                                    id: 'REQUEST.GET.PURCHASEPROPOSALITEMS.PROPOSALFILEURL',
                                  })}
                                </label>
                                <div className='col-lg-8 fv-row'>
                                  <a
                                    href={detail?.purchaseProposalConfirmedItem?.proposalFileUrl}
                                    target='_blank'
                                    rel='noreferrer'
                                    className='d-flex align-items-center'
                                  >
                                    <div className='symbol symbol-40px me-2'>
                                      <span className='symbol-label'>
                                        <img
                                          src={toAbsoluteUrl(
                                            '/images/icons/duotune/files/fil021.svg'
                                          )}
                                          className='h-50 align-self-center'
                                          alt=''
                                        />
                                      </span>
                                    </div>
                                  </a>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </Tab>
                    )}

                    {detail?.purchaseRequestItems?.length > 0 && (
                      <Tab
                        eventKey='purchaseRequestItems'
                        title={intl.formatMessage({id: 'REQUEST.GET.PURCHASEREQUESTITEMS'})}
                      >
                        <DataTables
                          columns={[
                            {
                              title: intl.formatMessage({
                                id: 'REQUEST.GET.PURCHASEREQUESTITEMS.PRODUCTNAME',
                              }),
                              prop: 'productName',
                              isSortable: true,
                              isFilterable: true,
                            },
                            {
                              title: intl.formatMessage({
                                id: 'REQUEST.GET.PURCHASEREQUESTITEMS.AMOUNT',
                              }),
                              prop: 'amount',
                              isSortable: true,
                              isFilterable: true,
                            },
                            {
                              title: intl.formatMessage({
                                id: 'REQUEST.GET.PURCHASEREQUESTITEMS.PRODUCTUNITNAME',
                              }),
                              prop: 'productUnitName',
                              isSortable: true,
                              isFilterable: true,
                            },
                            {
                              title: intl.formatMessage({
                                id: 'REQUEST.GET.PURCHASEREQUESTITEMS.DESCRIPTION',
                              }),
                              prop: 'description',
                              isSortable: true,
                              isFilterable: true,
                            },
                          ]}
                          data={detail?.purchaseRequestItems}
                          exportTitle={intl.formatMessage({id: 'REQUEST.GET.PURCHASEREQUESTITEMS'})}
                        />
                      </Tab>
                    )}

                    <Tab
                      eventKey='purchaseProposalItems'
                      title={intl.formatMessage({id: 'REQUEST.GET.PURCHASEPROPOSALITEMS'})}
                    >
                      <PurchaseProposal
                        data={detail?.purchaseProposalItems}
                        id={id}
                        canApprove={detail?.canApprove}
                      />
                    </Tab>

                    <Tab
                      eventKey='purchaseProposalRequestItems'
                      title={intl.formatMessage({id: 'REQUEST.GET.PURCHASEPROPOSALREQUESTITEMS'})}
                    >
                      <PurchaseProposalRequest
                        data={detail?.purchaseProposalRequestItems}
                        id={id}
                      />
                    </Tab>
                  </Tabs>
                </KTCardBody>
              </KTCard>
            </>
          )}
        </div>

        <div className='col-xl-3'>
          <KTCard className='mb-5'>
            <KTCardHeader
              title={intl.formatMessage({id: 'REQUEST.CREATE.PURCHASEREQUESTSTATUSID'})}
              collapsed={isCollapse}
              onCollapse={(event) => setIsCollapse(event)}
            />

            <KTCardBody className='ps-10 pe-10 pt-5 pb-0' collapseble isShow={isCollapse}>
              <form
                className='form position-relative mb-6'
                onSubmit={formik.handleSubmit}
                noValidate
              >
                <div className='row mb-6'>
                  <label className='col-lg-12 fw-bold fs-6 mb-2 required'>
                    {intl.formatMessage({id: 'REALESTATE.CREATE.ESTATESTATE'})}
                  </label>
                  <div className='col-lg-12 fv-row fv-plugins-icon-container'>
                    <Select
                      data={PurchaseRequestStatus}
                      required
                      {...formik.getFieldProps('PurchaseRequestStatusId')}
                      onChange={(item) =>
                        formik.setFieldValue('PurchaseRequestStatusId', Number(item?.value))
                      }
                      textType={'intl'}
                    />
                    {formik.touched.PurchaseRequestStatusId &&
                      formik.errors.PurchaseRequestStatusId && (
                        <div className='fv-plugins-message-container'>
                          <div className='fv-help-block'>
                            <span role='alert'>{formik.errors.PurchaseRequestStatusId}</span>
                          </div>
                        </div>
                      )}
                  </div>
                </div>

                <div className='row mb-6'>
                  <label className='col-lg-12 fw-bold fs-6 mb-2'>
                    {intl.formatMessage({id: 'REALESTATE.CREATE.EVALUATIONRESULT'})}
                  </label>
                  <div className='col-lg-12 fv-row fv-plugins-icon-container'>
                    <textarea
                      className='form-control form-control-solid mb-3 mb-lg-0'
                      autoComplete='off'
                      disabled={formik.isSubmitting}
                      {...formik.getFieldProps('EvaluationResult')}
                    />
                  </div>
                </div>

                <div className='text-center'>
                  <button
                    type='submit'
                    className='btn btn-primary'
                    data-kt-users-modal-action='submit'
                    disabled={formik.isSubmitting || !formik.isValid || !formik.touched}
                  >
                    <span className='indicator-label'>
                      {intl.formatMessage({id: 'BUTTON.SUBMIT'})}
                    </span>
                    {formik.isSubmitting && (
                      <span className='indicator-progress'>
                        {intl.formatMessage({id: 'PLEASE'})}
                        <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                      </span>
                    )}
                  </button>
                </div>
              </form>
            </KTCardBody>
          </KTCard>

          <Notes SourceId={Number(id)} SourceTypeId={SourceType.PurchaseRequest} />
        </div>
      </div>

      <DeleteModal
        onClick={handleDelete}
        onOpen={show}
        onClose={() => setShow(false)}
        error={error}
      />

      <ArchiveModal
        onClick={handleArchive}
        onOpen={showArchive}
        onClose={() => setShowArchive(false)}
        error={error}
      />

      {loading && <Loading />}
    </>
  )
}
export {Detail}
